import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { formatDate } from "../utils/format";
import { toast } from "sonner";
import { Plus, CheckCircle2, Circle, XCircle, X, Users as UsersIcon, Tag, Trash2, Save } from "lucide-react";
import GoogleAdsConnectorPanel from "../components/GoogleAdsConnectorPanel";
import GoogleSheetsConnectorPanel from "../components/GoogleSheetsConnectorPanel";
import AlertThresholdsPanel from "../components/AlertThresholdsPanel";
import RolePermissionsPanel from "../components/RolePermissionsPanel";
import BulkLeadDateEditor from "../components/BulkLeadDateEditor";

const TABS = [
  { id: "users", label: "Users & Roles" },
  { id: "role-permissions", label: "Role Permissions" },
  { id: "ui-settings", label: "UI Settings" },
  { id: "lead-statuses", label: "Lead Statuses" },
  { id: "alert-thresholds", label: "Alert Thresholds" },
  { id: "ai", label: "AI Providers" },
  { id: "benchmarks", label: "Benchmarks" },
  { id: "connectors", label: "Connectors" },
  { id: "data-tools", label: "Data Tools" },
  { id: "audit", label: "Audit Log" },
];
const VALID_TAB_IDS = new Set(TABS.map((t) => t.id));

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Honour deep links like /admin?tab=connectors. Default to "users" otherwise.
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return t && VALID_TAB_IDS.has(t) ? t : "users";
  })();
  const [tab, setTab] = useState(initialTab);

  // Keep URL in sync when the user clicks a tab — preserves shareable links
  // without forcing a full page navigation.
  const selectTab = (next) => {
    setTab(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "users") sp.delete("tab"); else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  // If the URL changes externally (back/forward, another LiveEmptyState CTA),
  // reflect it.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && VALID_TAB_IDS.has(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});
  const [connectors, setConnectors] = useState([]);
  const [changeLog, setChangeLog] = useState([]);
  const [newProv, setNewProv] = useState({ provider: "claude", model: "claude-sonnet-4-5", api_key: "", enabled: true, is_default: false });
  const [assignUser, setAssignUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [uiSettings, setUiSettings] = useState({});

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/ai-providers").then((r) => setProviders(r.data));
    api.get("/admin/benchmarks").then((r) => setBenchmarks(r.data));
    api.get("/admin/connectors").then((r) => setConnectors(r.data));
    api.get("/admin/change-log").then((r) => setChangeLog(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/clients/accounts/all").then((r) => setAccounts(r.data));
    api.get("/campaigns").then((r) => setCampaigns(r.data));
    api.get("/admin/lead-statuses").then((r) => setLeadStatuses(r.data.statuses));
    api.get("/admin/ui-settings").then((r) => setUiSettings(r.data));
  }, []);

  const saveUiSettings = async () => {
    try {
      await api.put("/admin/ui-settings", uiSettings);
      toast.success("UI settings saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const saveBenchmarks = async () => {
    await api.put("/admin/benchmarks", benchmarks);
    toast.success("Benchmarks updated");
  };

  const addProvider = async () => {
    try {
      await api.post("/admin/ai-providers", newProv);
      toast.success("AI provider added");
      const r = await api.get("/admin/ai-providers"); setProviders(r.data);
      setNewProv({ provider: "claude", model: "claude-sonnet-4-5", api_key: "", enabled: true, is_default: false });
    } catch (e) { toast.error("Failed to add"); }
  };
  const removeProvider = async (id) => { await api.delete(`/admin/ai-providers/${id}`); setProviders(providers.filter((p) => p.id !== id)); };

  const openAssign = async (u) => {
    const { data } = await api.get(`/admin/users/${u.id}`);
    setAssignUser({
      ...u,
      assigned_client_ids: data.assigned_client_ids || [],
      assigned_account_ids: data.assigned_account_ids || [],
      assigned_campaign_ids: data.assigned_campaign_ids || [],
    });
  };

  const saveAssignments = async () => {
    try {
      await api.put(`/admin/users/${assignUser.id}/assignments`, {
        assigned_client_ids: assignUser.assigned_client_ids,
        assigned_account_ids: assignUser.assigned_account_ids,
        assigned_campaign_ids: assignUser.assigned_campaign_ids,
      });
      toast.success("Assignments saved");
      setAssignUser(null);
      const r = await api.get("/admin/users"); setUsers(r.data);
    } catch (e) { toast.error("Save failed"); }
  };

  const patchUserRole = async (id, role) => {
    await api.patch(`/admin/users/${id}`, { role });
    toast.success("Role updated");
    const r = await api.get("/admin/users"); setUsers(r.data);
  };

  // ----- Users CRUD state + handlers (must live at component-body scope) -----
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [editUser, setEditUser] = useState(null);

  const createUser = async () => {
    try {
      await api.post("/admin/users", newUser);
      toast.success("User created");
      setNewUser({ name: "", email: "", password: "", role: "viewer" });
      setShowNewUser(false);
      const r = await api.get("/admin/users"); setUsers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    }
  };

  const saveEditUser = async () => {
    if (!editUser) return;
    try {
      const payload = { name: editUser.name, email: editUser.email };
      if (editUser.password) payload.password = editUser.password;
      await api.patch(`/admin/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      const r = await api.get("/admin/users"); setUsers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteUser = async (u) => {
    if (!u || !window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      const r = await api.get("/admin/users"); setUsers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const saveLeadStatuses = async () => {
    try {
      await api.put("/admin/lead-statuses", { statuses: leadStatuses });
      toast.success("Lead statuses saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const addStatus = () => {
    setLeadStatuses([...leadStatuses, { key: `new_${Date.now()}`, label: "New Status", color: "primary", order: leadStatuses.length, is_terminal: false }]);
  };

  return (
    <Layout title="Admin Control Panel" subtitle="System configuration">
      <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => selectTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{users.length} user{users.length !== 1 ? "s" : ""}</div>
            <button
              onClick={() => setShowNewUser(true)}
              data-testid="user-add-btn"
              className="h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Add user
            </button>
          </div>

          {showNewUser && (
            <div className="bg-card border border-border rounded-sm p-5 max-w-2xl" data-testid="user-create-form">
              <div className="font-heading text-lg mb-4 tracking-tight">Create user</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Full name" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="user-create-name" />
                <input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email" type="email" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="user-create-email" />
                <input value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password (min 6)" type="password" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="user-create-pw" />
                <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="user-create-role">
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="analyst">Analyst</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={createUser} data-testid="user-create-save" className="h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm">Create</button>
                <button onClick={() => setShowNewUser(false)} className="h-9 px-4 border border-border text-sm rounded-sm">Cancel</button>
              </div>
            </div>
          )}

          {editUser && (
            <div className="bg-card border border-border rounded-sm p-5 max-w-2xl" data-testid="user-edit-form">
              <div className="font-heading text-lg mb-4 tracking-tight">Edit {editUser.email}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} placeholder="Full name" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="user-edit-name" />
                <input value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} placeholder="Email" type="email" className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="user-edit-email" />
                <input value={editUser.password || ""} onChange={(e) => setEditUser({ ...editUser, password: e.target.value })} placeholder="New password (leave blank to keep)" type="password" className="h-10 px-3 border border-border rounded-sm text-sm md:col-span-2" data-testid="user-edit-pw" />
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={saveEditUser} data-testid="user-edit-save" className="h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm">Save</button>
                <button onClick={() => setEditUser(null)} className="h-9 px-4 border border-border text-sm rounded-sm">Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="users-table">
            <table className="w-full data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Assignments</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium">{u.name}</td>
                    <td className="font-mono text-xs">{u.email}</td>
                    <td>
                      <select value={u.role} onChange={(e) => patchUserRole(u.id, e.target.value)} className="bg-transparent border border-border text-[11px] rounded-sm px-1.5 py-0.5 uppercase" data-testid={`user-role-${u.id}`}>
                        <option value="super_admin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                        <option value="analyst">Analyst</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </td>
                    <td className="text-xs">
                      <div className="flex gap-2">
                        <span className="text-muted-foreground">{u.assigned_client_ids?.length || 0} clients</span>
                        <span className="text-muted-foreground">· {u.assigned_account_ids?.length || 0} accts</span>
                        <span className="text-muted-foreground">· {u.assigned_campaign_ids?.length || 0} camps</span>
                      </div>
                    </td>
                    <td className="text-xs text-muted-foreground">{formatDate(u.created_at)}</td>
                    <td>
                      <div className="flex items-center gap-3 justify-end">
                        <button onClick={() => setEditUser({ id: u.id, name: u.name, email: u.email, password: "" })} data-testid={`user-edit-${u.id}`} className="text-xs text-primary hover:underline">Edit</button>
                        <button onClick={() => openAssign(u)} data-testid={`assign-user-${u.id}`} className="text-xs text-primary hover:underline">Assignments</button>
                        <button onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`} className="text-xs text-[hsl(var(--critical))] hover:underline">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "ui-settings" && uiSettings && Object.keys(uiSettings).length > 0 && (
        <div className="bg-card border border-border rounded-sm p-5 max-w-2xl" data-testid="ui-settings-panel">
          <div className="label-mono mb-1">Public-facing controls</div>
          <div className="font-heading text-lg mb-4 tracking-tight">Login & branding</div>
          <p className="text-xs text-muted-foreground mb-5">These flags are exposed unauthenticated via <code className="font-mono text-[11px]">/api/public/ui-settings</code>. Only safe, non-sensitive flags live here.</p>

          <div className="space-y-5">
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="toggle-demo-creds">
              <input
                type="checkbox"
                checked={!!uiSettings.show_demo_credentials}
                onChange={(e) => setUiSettings({ ...uiSettings, show_demo_credentials: e.target.checked })}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium">Show demo credentials on login page</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  When off, the three hard-coded demo accounts are hidden and the email/password inputs start empty. Recommended for production.
                </div>
              </div>
            </label>

            <Field label="Brand name">
              <input
                value={uiSettings.brand_name || ""}
                onChange={(e) => setUiSettings({ ...uiSettings, brand_name: e.target.value })}
                className="w-full h-10 px-3 border border-border rounded-sm text-sm"
                data-testid="ui-brand-name"
              />
            </Field>

            <Field label="Login tagline">
              <textarea
                value={uiSettings.login_tagline || ""}
                onChange={(e) => setUiSettings({ ...uiSettings, login_tagline: e.target.value })}
                className="w-full h-16 px-3 py-2 border border-border rounded-sm text-sm"
                data-testid="ui-login-tagline"
              />
            </Field>

            <div className="border-t border-border pt-5 space-y-3">
              <div className="label-mono">Maintenance banner</div>
              <p className="text-xs text-muted-foreground -mt-2">Shown at the top of every authenticated page. Use for API outages, scheduled maintenance, or urgent notices.</p>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-maintenance">
                <input type="checkbox" checked={!!uiSettings.maintenance_banner_enabled} onChange={(e) => setUiSettings({ ...uiSettings, maintenance_banner_enabled: e.target.checked })} />
                <div className="text-sm font-medium">Show maintenance banner</div>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <input value={uiSettings.maintenance_banner_message || ""} onChange={(e) => setUiSettings({ ...uiSettings, maintenance_banner_message: e.target.value })} placeholder="Banner message…" className="md:col-span-3 h-10 px-3 border border-border rounded-sm text-sm" data-testid="maint-message" />
                <select value={uiSettings.maintenance_banner_severity || "info"} onChange={(e) => setUiSettings({ ...uiSettings, maintenance_banner_severity: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="maint-severity">
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
          </div>

          <button
            onClick={saveUiSettings}
            data-testid="ui-settings-save"
            className="mt-5 h-10 px-5 bg-primary text-primary-foreground text-sm font-medium rounded-sm flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> Save UI Settings
          </button>
        </div>
      )}

      {tab === "lead-statuses" && (
        <div className="bg-card border border-border rounded-sm p-5 max-w-3xl" data-testid="lead-statuses-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono">Lead lifecycle</div>
              <div className="font-heading text-lg tracking-tight">Editable lead statuses</div>
              <p className="text-xs text-muted-foreground mt-1">Used by the Leads module dropdowns and by status-history logging. Deleting or renaming affects existing leads.</p>
            </div>
            <button onClick={addStatus} data-testid="lead-status-add" className="h-9 px-3 bg-primary text-primary-foreground text-xs font-medium rounded-sm flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add</button>
          </div>
          <div className="space-y-2">
            {leadStatuses.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input value={s.key} onChange={(e) => { const ns = [...leadStatuses]; ns[i] = { ...s, key: e.target.value }; setLeadStatuses(ns); }} placeholder="key" className="col-span-3 h-9 px-2 border border-border rounded-sm text-xs font-mono" data-testid={`ls-key-${i}`} />
                <input value={s.label} onChange={(e) => { const ns = [...leadStatuses]; ns[i] = { ...s, label: e.target.value }; setLeadStatuses(ns); }} placeholder="label" className="col-span-3 h-9 px-2 border border-border rounded-sm text-xs" data-testid={`ls-label-${i}`} />
                <select value={s.color} onChange={(e) => { const ns = [...leadStatuses]; ns[i] = { ...s, color: e.target.value }; setLeadStatuses(ns); }} className="col-span-2 h-9 px-2 border border-border rounded-sm text-xs">
                  <option value="primary">primary</option><option value="success">success</option><option value="warning">warning</option><option value="critical">critical</option><option value="muted">muted</option><option value="ai">ai</option>
                </select>
                <input type="number" value={s.order} onChange={(e) => { const ns = [...leadStatuses]; ns[i] = { ...s, order: parseInt(e.target.value, 10) }; setLeadStatuses(ns); }} className="col-span-1 h-9 px-2 border border-border rounded-sm text-xs tabular" />
                <label className="col-span-2 text-xs flex items-center gap-1"><input type="checkbox" checked={s.is_terminal} onChange={(e) => { const ns = [...leadStatuses]; ns[i] = { ...s, is_terminal: e.target.checked }; setLeadStatuses(ns); }} /> terminal</label>
                <button onClick={() => setLeadStatuses(leadStatuses.filter((_, idx) => idx !== i))} data-testid={`ls-del-${i}`} className="col-span-1 text-[hsl(var(--critical))] hover:bg-[hsl(var(--critical))]/10 rounded-sm h-9 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <button onClick={saveLeadStatuses} data-testid="ls-save" className="mt-4 h-10 px-5 bg-primary text-primary-foreground text-sm font-medium rounded-sm flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save Statuses</button>
        </div>
      )}

      {tab === "alert-thresholds" && (
        <div data-testid="alert-thresholds-tab"><AlertThresholdsPanel /></div>
      )}

      {tab === "role-permissions" && (
        <div data-testid="role-permissions-tab"><RolePermissionsPanel /></div>
      )}

      {tab === "data-tools" && (
        <div data-testid="data-tools-tab"><BulkLeadDateEditor /></div>
      )}

      {tab === "ai" && (
        <div className="space-y-4" data-testid="ai-providers-panel">
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-1">Add provider</div>
            <div className="font-heading text-lg mb-4 tracking-tight">Connect Claude / OpenAI / Gemini</div>
            <p className="text-xs text-muted-foreground mb-4">API keys are encrypted and never shown after save. The platform stays provider-agnostic.</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <select value={newProv.provider} onChange={(e) => setNewProv({ ...newProv, provider: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm">
                <option value="claude">Claude</option><option value="openai">OpenAI</option><option value="gemini">Gemini</option><option value="mock">Mock</option>
              </select>
              <input placeholder="Model" value={newProv.model} onChange={(e) => setNewProv({ ...newProv, model: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" />
              <input placeholder="API Key" value={newProv.api_key} onChange={(e) => setNewProv({ ...newProv, api_key: e.target.value })} type="password" className="h-10 px-3 border border-border rounded-sm text-sm" />
              <button onClick={addProvider} data-testid="add-provider-btn" className="h-10 bg-primary text-primary-foreground text-sm font-medium rounded-sm flex items-center justify-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <table className="w-full data-table">
              <thead><tr><th>Provider</th><th>Model</th><th>API Key</th><th>Default</th><th>Enabled</th><th></th></tr></thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p.id}>
                    <td><StatusBadge tone="ai">{p.provider}</StatusBadge></td>
                    <td className="font-mono text-xs">{p.model}</td>
                    <td className="font-mono text-xs">{p.api_key_masked}</td>
                    <td>{p.is_default ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> : <Circle className="w-4 h-4 text-muted-foreground" />}</td>
                    <td>{p.enabled ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</td>
                    <td><button onClick={() => removeProvider(p.id)} className="text-xs text-[hsl(var(--critical))] hover:underline">Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "benchmarks" && benchmarks && Object.keys(benchmarks).length > 0 && (
        <div className="bg-card border border-border rounded-sm p-5 max-w-2xl" data-testid="benchmarks-panel">
          <div className="label-mono mb-1">Performance thresholds</div>
          <div className="font-heading text-lg mb-4 tracking-tight">Green / Amber / Red boundaries</div>
          <div className="grid grid-cols-2 gap-4">
            {[["ctr_green", "CTR · Green ≥ (%)"], ["ctr_amber", "CTR · Amber ≥ (%)"], ["cpc_green", "CPC · Green ≤ (₹)"], ["cpc_amber", "CPC · Amber ≤ (₹)"], ["cpl_green", "CPL · Green ≤ (₹)"], ["cpl_amber", "CPL · Amber ≤ (₹)"]].map(([k, label]) => (
              <div key={k}>
                <label className="label-mono block mb-1.5">{label}</label>
                <input type="number" value={benchmarks[k] ?? 0} onChange={(e) => setBenchmarks({ ...benchmarks, [k]: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-border rounded-sm text-sm tabular" />
              </div>
            ))}
          </div>
          <button onClick={saveBenchmarks} data-testid="save-benchmarks-btn" className="mt-5 h-10 px-5 bg-primary text-primary-foreground text-sm font-medium rounded-sm">Save Changes</button>
        </div>
      )}

      {tab === "connectors" && (
        <div className="space-y-6" data-testid="connectors-panel">
          <GoogleAdsConnectorPanel />
          <div className="border-t border-border pt-6"><GoogleSheetsConnectorPanel /></div>
          <div>
            <div className="label-mono mb-2">Other connectors</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {connectors.filter((c) => c.id !== "google_ads" && c.id !== "google_sheets").map((c) => (
                <div key={c.id} className={`bg-card border border-border rounded-sm p-5 ${c.connected ? "kpi-border-success" : "kpi-border-muted"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-heading font-medium">{c.name}</div>
                    <StatusBadge tone={c.connected ? "success" : "muted"}>{c.connected ? "Mock Connected" : "Disconnected"}</StatusBadge>
                  </div>
                  <div className="text-xs text-muted-foreground mb-3">{c.note}</div>
                  <div className="text-xs flex justify-between"><span className="label-mono">Mode</span><span className="uppercase font-semibold tracking-wider">{c.mode}</span></div>
                  <div className="text-xs flex justify-between mt-1"><span className="label-mono">Accounts</span><span className="font-semibold tabular">{c.accounts_synced}</span></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "audit" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="audit-log-table">
          <table className="w-full data-table">
            <thead><tr><th>Entity</th><th>Action</th><th>Before → After</th><th>User</th><th>When</th></tr></thead>
            <tbody>
              {changeLog.map((l) => (
                <tr key={l.id}>
                  <td className="font-mono text-xs">{l.entity_type}</td>
                  <td><StatusBadge tone="primary">{l.action}</StatusBadge></td>
                  <td className="font-mono text-[11px] text-muted-foreground">{l.before_value} → {l.after_value}</td>
                  <td>{l.user_name}</td>
                  <td className="text-xs text-muted-foreground">{formatDate(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {changeLog.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No audit events yet.</div>}
        </div>
      )}

      {/* Assignments Modal */}
      {assignUser && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAssignUser(null)}>
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="assignments-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-heading text-lg font-medium flex items-center gap-2"><UsersIcon className="w-5 h-5 text-primary" /> Assignments · {assignUser.name}</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{assignUser.email} · {assignUser.role}</div>
              </div>
              <button onClick={() => setAssignUser(null)}><X className="w-4 h-4" /></button>
            </div>

            <AssignGroup
              title="Clients" items={clients} keyField="id" labelField="name"
              selected={assignUser.assigned_client_ids}
              onChange={(next) => setAssignUser({ ...assignUser, assigned_client_ids: next })}
              testPrefix="assign-client"
            />
            <AssignGroup
              title="Google Ads Accounts" items={accounts} keyField="id"
              labelFormatter={(a) => `${a.name} · ${a.google_customer_id} (${a.client_name || "—"})`}
              selected={assignUser.assigned_account_ids}
              onChange={(next) => setAssignUser({ ...assignUser, assigned_account_ids: next })}
              testPrefix="assign-account"
            />
            <AssignGroup
              title="Campaigns" items={campaigns} keyField="id"
              labelFormatter={(c) => `${c.name} (${c.client_name || "—"})`}
              selected={assignUser.assigned_campaign_ids}
              onChange={(next) => setAssignUser({ ...assignUser, assigned_campaign_ids: next })}
              testPrefix="assign-campaign"
            />

            <div className="flex gap-2 mt-5">
              <button onClick={saveAssignments} data-testid="save-assignments-btn" className="flex-1 h-10 bg-primary text-primary-foreground text-sm font-medium rounded-sm">Save Assignments</button>
              <button onClick={() => setAssignUser(null)} className="flex-1 h-10 border border-border text-sm rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="label-mono mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function AssignGroup({ title, items, keyField, labelField, labelFormatter, selected, onChange, testPrefix }) {
  const [q, setQ] = useState("");
  const visible = items.filter((it) => {
    const label = labelFormatter ? labelFormatter(it) : it[labelField];
    return !q || label.toLowerCase().includes(q.toLowerCase());
  });
  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono flex items-center gap-2"><Tag className="w-3.5 h-3.5" /> {title} <span className="text-muted-foreground">({selected.length} / {items.length})</span></div>
        <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-7 px-2 border border-border rounded-sm text-xs w-40" />
      </div>
      <div className="border border-border rounded-sm max-h-44 overflow-y-auto">
        {visible.map((it) => (
          <label key={it[keyField]} className="flex items-center gap-2 px-3 py-1.5 border-b border-border last:border-b-0 hover:bg-secondary/30 cursor-pointer">
            <input type="checkbox" checked={selected.includes(it[keyField])} onChange={() => toggle(it[keyField])} data-testid={`${testPrefix}-${it[keyField]}`} />
            <span className="text-sm">{labelFormatter ? labelFormatter(it) : it[labelField]}</span>
          </label>
        ))}
        {visible.length === 0 && <div className="p-3 text-xs text-muted-foreground">No items.</div>}
      </div>
    </div>
  );
}
